import { centsToDollars, formatDollars } from '@fruitful/fruitils';
import {
    ApexAccountState,
    ReadableApexAccountRegistrationType,
    type WeightedSecurity,
} from '@fruitful/types';
import { Redirect, router, useLocalSearchParams } from 'expo-router';
import {
    BodyMediumRegular,
    FRUITFUL_ICON_KEY,
    FruitfulCustomIcon,
    FullScreenSpinner,
    LastUpdated,
    ScreenContainer,
    XStack,
    YStack,
} from 'smoothie';

import { useTrackViewOnFocus } from '../../../events/hooks/useTrackViewOnFocus';
import { useGetApexAccountDetails } from '../../hooks/useGetApexAccountDetails';
import { useGetApexPositions } from '../../hooks/useGetApexPositions';
import { ApexPortfolioChart } from './ApexPortfolioChart';
import { PortfolioAssetCategory } from './PortfolioAssetCategory';

export const ApexPortfolio = () => {
    useTrackViewOnFocus('Investment/Portfolio');

    const { apexAccountId } = useLocalSearchParams<{
        apexAccountId: string;
    }>();

    const { isLoading: isLoadingAccount, data: apexAccount } =
        useGetApexAccountDetails(apexAccountId);

    const {
        data: apexPositions,
        isLoading: isLoadingPositions,
        dataUpdatedAt,
    } = useGetApexPositions(
        apexAccount?.apexAccountId ?? '',
        apexAccount != null,
    );

    if (apexAccount?.state !== ApexAccountState.OPEN) {
        router.navigate('/home/investment');
        return;
    }

    const { realtimeBalanceData } = apexAccount;
    const accountEquity = centsToDollars(
        realtimeBalanceData.accountEquityCents,
    );
    const positionMarketValue = centsToDollars(
        realtimeBalanceData.positionMarketValueCents,
    );
    const cash = centsToDollars(realtimeBalanceData.cashCents);

    if (isLoadingAccount || isLoadingPositions) {
        return <FullScreenSpinner />;
    }

    if (apexPositions == null) {
        return (
            <Redirect
                href={`/investments/account-overview?apexAccountId=${apexAccountId}`}
            />
        );
    }

    const weightedPositions = apexPositions?.reduce<{
        cash: WeightedSecurity[];
        equities: WeightedSecurity[];
        fixedIncome: WeightedSecurity[];
        unassigned: WeightedSecurity[];
    }>(
        (acc, cur: WeightedSecurity) => {
            switch (cur.category) {
                case 'cash':
                    acc.cash.push(cur);
                    break;
                case 'equities':
                    acc.equities.push(cur);
                    break;
                case 'fixedIncome':
                    acc.fixedIncome.push(cur);
                    break;
                case 'unassigned':
                    acc.unassigned.push(cur);
                    break;
            }
            return acc;
        },
        { cash: [], equities: [], fixedIncome: [], unassigned: [] },
    );

    return (
        <ScreenContainer title="Composition" hasBackButton>
            <YStack width="100%" alignItems="center" gap={24}>
                <XStack
                    alignItems="center"
                    gap={12}
                    backgroundColor="$gray200"
                    width="100%"
                    padding={16}
                    borderTopLeftRadius={16}
                    borderTopRightRadius={16}
                >
                    <FruitfulCustomIcon
                        color={apexAccount?.color ?? 'peach'}
                        icon={apexAccount?.icon ?? FRUITFUL_ICON_KEY}
                        useAltColors={true}
                        size="xs"
                    />
                    <BodyMediumRegular>
                        {apexAccount?.nickname ?? apexAccount?.accountNumber}
                    </BodyMediumRegular>
                </XStack>
                <XStack justifyContent="center">
                    <ApexPortfolioChart
                        portfolio={weightedPositions}
                        availableLiquidityAmount={formatDollars(accountEquity)}
                    />
                </XStack>
                <YStack width="100%" padding={16} gap={12}>
                    <XStack justifyContent="space-between">
                        <BodyMediumRegular>Account type</BodyMediumRegular>
                        <BodyMediumRegular>
                            {
                                ReadableApexAccountRegistrationType[
                                    apexAccount.registrationType
                                ]
                            }
                        </BodyMediumRegular>
                    </XStack>
                    <XStack justifyContent="space-between">
                        <BodyMediumRegular>Portfolio</BodyMediumRegular>
                        <BodyMediumRegular>
                            {apexAccount?.apexPortfolio}
                        </BodyMediumRegular>
                    </XStack>
                    <XStack justifyContent="space-between">
                        <BodyMediumRegular>Risk level</BodyMediumRegular>
                        <BodyMediumRegular>
                            {apexAccount?.apexRiskLevel} out of 10
                        </BodyMediumRegular>
                    </XStack>
                    <XStack justifyContent="space-between">
                        <BodyMediumRegular>Invested balance</BodyMediumRegular>
                        <BodyMediumRegular>
                            {formatDollars(positionMarketValue)}
                        </BodyMediumRegular>
                    </XStack>
                    <XStack justifyContent="space-between">
                        <BodyMediumRegular>Available balance</BodyMediumRegular>
                        <BodyMediumRegular>
                            {formatDollars(cash)}
                        </BodyMediumRegular>
                    </XStack>
                </YStack>
                {weightedPositions.equities.length > 0 && (
                    <PortfolioAssetCategory
                        securities={weightedPositions.equities}
                        name="Equities"
                        type="equities"
                    />
                )}
                {weightedPositions.fixedIncome.length > 0 && (
                    <PortfolioAssetCategory
                        securities={weightedPositions.fixedIncome}
                        name="Fixed Income"
                        type="fixedIncome"
                    />
                )}
                {weightedPositions.cash.length > 0 && (
                    <PortfolioAssetCategory
                        securities={weightedPositions.cash}
                        name="Cash Equivalents"
                        type="cash"
                    />
                )}
                {weightedPositions.unassigned.length > 0 && (
                    <PortfolioAssetCategory
                        securities={weightedPositions.unassigned}
                        name="Unassigned securities"
                        type="unassigned"
                    />
                )}
                <LastUpdated dataUpdatedAt={dataUpdatedAt} />
            </YStack>
        </ScreenContainer>
    );
};
